import { Either, Maybe } from 'monet';
import LeftResponse from '@/storage/LeftResponse';
import { getISODateStringByDate } from '@/mixins/dateMixin';

/**
 * @description Статусы задач
 * @type Array
 */
export const statuses = [
  { value: 'new', title: 'Новая' },
  { value: 'work', title: 'В работе' },
  { value: 'canceled', title: 'Отказ' },
  { value: 'completed', title: 'Решена' },
];

/**
 * @description Отобразить статус в читаемый вид
 * @param {String} status
 */
export function mapStatus(status) {
  return statuses.find((item) => item.value === status).title;
}

/**
 * @description Задача для показа по запросу и редактирования
 * @param {String} authorInitials
 * @param {String} authorName
 * @param {String} authorPhotoUrl
 * @param {String} createdAt
 * @param {String} deadLineInFormat
 * @param {String} deadLine
 * @param {String} description
 * @param {Number} id
 * @param {boolean} isOverdue
 * @param {Number} performerId
 * @param {String} performerInitials
 * @param {String} performerName
 * @param {String} performerPhotoUrl
 * @param {String} status
 * @param {String} title
 * @param {number} patientId
 * @param {String} patientName
 * @constructor
 */
export function TaskForEdit(
  authorInitials,
  authorName,
  authorPhotoUrl,
  createdAt,
  deadLineInFormat,
  deadLine,
  description,
  id,
  isOverdue,
  performerId,
  performerInitials,
  performerName,
  performerPhotoUrl,
  status,
  title,
  patientId,
  patientName,
) {
  this.authorInitials = authorInitials;
  this.authorName = authorName;
  this.authorPhotoUrl = authorPhotoUrl;
  this.createdAt = createdAt;
  this.deadLineInFormat = deadLineInFormat;
  this.deadLine = deadLine;
  this.description = description;
  this.id = id;
  this.isOverdue = isOverdue;
  this.performerId = performerId;
  this.performerInitials = performerInitials;
  this.performerName = performerName;
  this.performerPhotoUrl = performerPhotoUrl;
  this.status = status;
  this.title = title;
  this.patientId = patientId;
  this.patientName = patientName;
}

/**
 * @description Задача для отображения в таблице
 * @param {Number} id
 * @param {Boolean} isAccessable
 * @param {Boolean} isOverdue
 * @param {String} title
 * @param {String} authorName
 * @param {String} performerName
 * @param {String} deadLine
 * @param {String} status
 * @param {String} createdAt
 * @constructor
 */
export function Task(
  id = 0,
  isAccessable = false,
  isOverdue = false,
  title = '',
  authorName = '',
  performerName = '',
  deadLine = '',
  status = '',
  createdAt = '',
) {
  this.id = id;
  this.isAccessable = isAccessable;
  this.isOverdue = isOverdue;
  this.title = title;
  this.authorName = authorName;
  this.performerName = performerName;
  this.deadLine = deadLine;
  this.status = status;
  this.createdAt = createdAt;
}

/**
 * @description Реквест для лист с задачами
 * @constructor
 * @param sort
 * @param authorId
 * @param performerId
 * @param createAtDate
 * @param deadLineDate
 */
export function ViewTaskListRequest(
  sort = { type: '', order: '' },
  authorId = 0,
  performerId = 0,
  createAtDate = [],
  deadLineDate = [],
) {
  this.sort_type = sort.type;
  this.sort_order = sort.order;
  this.author_id = authorId;
  this.performer_id = performerId;
  this.created_at_from = createAtDate.length > 1
    ? getISODateStringByDate(createAtDate[0])
    : '';
  this.created_at_to = createAtDate.length > 1
    ? getISODateStringByDate(createAtDate[1])
    : '';
  this.dead_line_from = deadLineDate.length > 1
    ? getISODateStringByDate(deadLineDate[0])
    : '';
  this.dead_line_to = deadLineDate.length > 1
    ? getISODateStringByDate(deadLineDate[1])
    : '';
}

/**
 * @description Запрос на редактирование/создание задачи
 * @param title
 * @param performerId
 * @param deadLine
 * @param status
 * @param description
 * @param patientId
 * @constructor
 */
export function TaskRequest(
  title,
  performerId,
  deadLine,
  status,
  description,
  patientId,
) {
  this.title = title;
  this.performer_id = performerId;
  this.dead_line = deadLine;
  this.status = status;
  this.description = description;
  this.patient_id = patientId;
}

/**
 * @description Шлюз работы с задачами
 * @param {Axios} axios
 * @constructor
 */
export default function TaskGateway(axios) {
  /**
   * @description Получить список исполнителей
   * @return {Promise<Either<any>>}
   */
  this.getTaskOptions = () => axios
    .get('/api/task-options').then(
      async ({ data }) => Either.right({
        performers: data.performers,
        patients: data.patients,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить число активных задач
   * @return {Promise<Maybe<unknown>>}
   */
  this.getActiveTaskCount = () => axios
    .get('/api/active-tasks/count')
    .then(async (response) => Maybe.fromEmpty(response.data));
  /**
   * @description Получить список задач
   * @return {Promise<Either<any>>}
   */
  this.getTaskList = (
    request,
  ) => axios
    .get('/api/tasks', {
      params: request,
    }).then(
      async (response) => {
        const tasks = response.data;
        const taskList = tasks.map((task) => new Task(
          task.id,
          task.is_accessable,
          task.is_overdue,
          task.title,
          task.author_name,
          task.performer_name,
          task.dead_line,
          task.status,
          task.created_at,
        ));
        return Either.right(taskList);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить задачу по идентификатору
   * @param {Number} id Идентификатор задачи
   * @return {Promise<Either<any>>}
   */
  this.getTaskById = (id) => axios
    .get(`/api/tasks/${id}`).then(
      async (response) => {
        const { task } = response.data;
        const taskForEdit = new TaskForEdit(
          task.author_initials,
          task.author_name,
          task.author_photo_url,
          task.created_at,
          task.dead_line_in_format,
          task.dead_line,
          task.description,
          task.id,
          task.is_overdue,
          task.performer_id,
          task.performer_initials,
          task.performer_name,
          task.performer_photo_url,
          task.status,
          task.title,
          task.patient_id,
          task.patient_name,
        );
        return Either.right(taskForEdit);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Создать задачу
   * @param {TaskRequest} request Объект запроса с набором данных
   * @param {string}      dealId  Идентификатор заявки.
   * @return {Promise<Either<any>>}
   */
  this.createTask = (request, dealId = '') => axios
    .post('/api/tasks', { deal_id: dealId, ...request })
    .then(
      async (response) => Either.right(response.data.message),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Редактировать задачу
   * @param {TaskRequest} request Объект запроса с набором данных
   * @param {Number} id Идентификатор задачи
   * @return {Promise<Either<any>>}
   */
  this.editTask = (request, id) => axios
    .put(`/api/tasks/${id}`, { ...request })
    .then(
      async (response) => Either.right(response.data.message),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Удалить задачу
   * @param {Number} id Идентификатор задачи
   * @return {Promise<Either<any>>}
   */
  this.deleteTask = (id) => axios
    .delete(`/api/tasks/${id}`)
    .then(
      async (response) => Either.right(response.data.message),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Удалить задачу
   * @param {Number} id Идентификатор задачи
   * @param {String} status Статус
   * @return {Promise<Either<any>>}
   */
  this.changeStatus = (id, status) => axios
    .put(`/api/tasks/${id}/status`, { status })
    .then(
      async (response) => Either.right(response.data.message),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
}
